import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Compass } from "lucide-react";
import { imgSrc, imgSet } from "@/lib/img";

export const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, y = 28, className, style, ...rest }) {
  return (
    <motion.div
      className={className}
      style={style}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.85, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function MaskedLines({ lines, className, lineClass, delay = 0, stagger = 0.16 }) {
  const norm = lines.map((l) => (Array.isArray(l) ? l : [l, null]));
  return (
    <span className={className}>
      {norm.map(([text, extra], i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em]">
          <motion.span
            className={"block " + (lineClass || "") + " " + (extra || "")}
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.15, delay: delay + i * stagger, ease: EASE }}
          >
            {text}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export function Marquee({ items, duration = 80, className }) {
  const Row = ({ hidden }) => (
    <div className="flex shrink-0 items-center" aria-hidden={hidden}>
      {items.map((it, i) => (
        <span key={i} className="flex items-center whitespace-nowrap">
          <span className={"font-serif text-[13vw] sm:text-[8vw] leading-none px-6 " + (i % 2 ? "text-copper italic" : "text-cream")}>{it}</span>
          <span className="inline-block h-3 w-3 rounded-full bg-copper mx-2" />
        </span>
      ))}
    </div>
  );
  return (
    <div className={"overflow-hidden border-y border-copper/20 py-8 sm:py-10 " + (className || "")}>
      <div className="flex w-max animate-marquee" style={{ animationDuration: `${duration}s` }}>
        <Row />
        <Row hidden />
      </div>
    </div>
  );
}

export function SafeImg({ src, alt, className, placeholderClass, width = 1200, widths = [400, 800, 1200], sizes = "(max-width: 768px) 100vw, 50vw" }) {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <div className={(placeholderClass || className || "") + " bg-obsidian2 flex items-center justify-center"}>
        <Compass className="h-8 w-8 text-copper/40" />
      </div>
    );
  }
  return (
    <img
      src={imgSrc(src, width)}
      srcSet={imgSet(src, widths)}
      sizes={sizes}
      alt={alt || ""}
      className={className}
      loading="lazy"
      decoding="async"
      onError={() => setErr(true)}
    />
  );
}

export function ParallaxImage({ src, alt, className, imgClass, strength = 80, sizes = "(max-width: 768px) 100vw, 50vw" }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-strength, strength]);
  return (
    <div ref={ref} className={"relative overflow-hidden " + (className || "")}>
      {src ? (
        <motion.img
          src={imgSrc(src, 1200)}
          srcSet={imgSet(src, [400, 800, 1200, 1600])}
          sizes={sizes}
          alt={alt || ""}
          style={{ y }}
          className={"w-full h-full object-cover " + (imgClass || "")}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className="w-full h-full bg-obsidian2 flex items-center justify-center" style={{ y }}>
          <Compass className="h-10 w-10 text-copper/50" />
        </div>
      )}
    </div>
  );
}
